import React, { useState, useEffect } from 'react';
import ProgressRing from './ProgressRing';

const PersonalityInsights = ({ 
  profile, 
  totalScore, 
  streak, 
  responses, 
  onContinue 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [animatedScores, setAnimatedScores] = useState({});

  const slides = [
    { type: 'celebration', title: 'Awesome! 🎉' },
    { type: 'score', title: 'Your Score' },
    { type: 'personality', title: 'Your Investment Style' },
    { type: 'insights', title: 'What This Means' },
    { type: 'next_steps', title: 'Ready to Invest!' }
  ];

  // Show confetti on mount
  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Animate scores
  useEffect(() => {
    if (currentSlide === 1) {
      setTimeout(() => {
        setAnimatedScores({
          totalScore: totalScore,
          streak: streak,
          accuracy: Math.round((responses.filter(r => r.points >= 15).length / responses.length) * 100)
        });
      }, 500);
    }
  }, [currentSlide, totalScore, streak, responses]);

  // Auto-advance slides
  useEffect(() => {
    if (currentSlide < slides.length - 1) {
      const timer = setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
      }, currentSlide === 0 ? 2000 : 4000); // First slide shorter
      return () => clearTimeout(timer);
    }
  }, [currentSlide, slides.length]);

  // Get personality badges
  const getPersonalityBadges = () => {
    const badges = [];
    
    if (profile.riskTolerance) {
      badges.push({
        icon: profile.riskTolerance.category === 'conservative' ? '🛡️' : 
              profile.riskTolerance.category === 'aggressive' ? '🚀' : '⚖️',
        text: `${profile.riskTolerance.category} Investor`,
        color: profile.riskTolerance.category === 'conservative' ? '#10B981' : 
               profile.riskTolerance.category === 'aggressive' ? '#EF4444' : '#3B82F6'
      });
    }

    if (profile.learningStyle?.pacePreference === 'step-by-step') {
      badges.push({
        icon: '📚',
        text: 'Step-by-Step Learner',
        color: '#8B5CF6'
      });
    }

    if (profile.behavioralTraits?.patience > 70) {
      badges.push({
        icon: '⏰',
        text: 'Patient Investor',
        color: '#F59E0B'
      });
    }

    if (totalScore > 100) {
      badges.push({
        icon: '🌟',
        text: 'High Achiever',
        color: '#EC4899'
      });
    }

    if (streak >= 5) {
      badges.push({
        icon: '🔥',
        text: 'On Fire!',
        color: '#F97316'
      });
    }

    return badges;
  };

  // Get personalized insights
  const getPersonalizedInsights = () => {
    const insights = [];
    
    if (profile.riskTolerance?.category === 'conservative') {
      insights.push({
        icon: '🛡️',
        title: 'Safety First',
        description: 'You prefer stable investments with steady growth. I\'ll show you blue-chip stocks and diversified funds.'
      });
    } else if (profile.riskTolerance?.category === 'aggressive') {
      insights.push({
        icon: '🚀',
        title: 'Growth Focused',
        description: 'You\'re comfortable with higher risks for bigger rewards. I\'ll introduce you to growth stocks and emerging markets.'
      });
    } else {
      insights.push({
        icon: '⚖️',
        title: 'Balanced Approach',
        description: 'You like a mix of safety and growth. I\'ll help you build a diversified portfolio.'
      });
    }

    if (profile.learningStyle?.pacePreference === 'step-by-step') {
      insights.push({
        icon: '🎓',
        title: 'Thorough Learner',
        description: 'I\'ll explain concepts step-by-step and make sure you understand before moving on.'
      });
    }

    if (profile.behavioralTraits?.patience > 70) {
      insights.push({
        icon: '🕰️',
        title: 'Long-term Thinker',
        description: 'Your patience is a superpower! I\'ll show you how compound interest works over time.'
      });
    }

    return insights;
  };

  const renderSlide = () => {
    const slide = slides[currentSlide];
    
    switch (slide.type) {
      case 'celebration':
        return (
          <div className="celebration-slide">
            <div className="celebration-content">
              <div className="celebration-emoji">🎉</div>
              <h2 className="celebration-title">Assessment Complete!</h2>
              <p className="celebration-subtitle">
                You've unlocked your personalized investment journey!
              </p>
              <div className="celebration-gideon">
                <div className="gideon-avatar-large">🎯</div>
                <div className="gideon-speech">
                  <div className="speech-bubble">
                    "Awesome job! Now I know exactly how to help you succeed! 🌟"
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'score':
        return (
          <div className="score-slide">
            <div className="score-header">
              <h2>Your Achievement Score</h2>
              <p>Here's how you did!</p>
            </div>
            
            <div className="score-cards">
              <div className="score-card main-score">
                <div className="score-icon">⭐</div>
                <div className="score-value">{animatedScores.totalScore || 0}</div>
                <div className="score-label">Total Points</div>
                <div className="score-progress">
                  <ProgressRing 
                    progress={Math.min((animatedScores.totalScore / 150) * 100, 100)}
                    size={60}
                    color="#F59E0B"
                    showPercentage={false}
                  />
                </div>
              </div>
              
              <div className="score-card">
                <div className="score-icon">🔥</div>
                <div className="score-value">{animatedScores.streak || 0}</div>
                <div className="score-label">Question Streak</div>
              </div>
              
              <div className="score-card">
                <div className="score-icon">🎯</div>
                <div className="score-value">{animatedScores.accuracy || 0}%</div>
                <div className="score-label">Great Choices</div>
              </div>
            </div>
            
            <div className="score-feedback">
              {totalScore >= 120 && "🌟 Outstanding performance!"}
              {totalScore >= 90 && totalScore < 120 && "🎉 Great job!"}
              {totalScore >= 60 && totalScore < 90 && "👍 Well done!"}
              {totalScore < 60 && "🌱 Good start!"}
            </div>
          </div>
        );

      case 'personality':
        return (
          <div className="personality-slide">
            <div className="personality-header">
              <h2>Your Investment Personality</h2>
              <p>Based on your responses, here's your unique investor profile:</p>
            </div>
            
            <div className="personality-badges-grid">
              {getPersonalityBadges().map((badge, index) => (
                <div 
                  key={index} 
                  className="personality-badge"
                  style={{ 
                    '--badge-color': badge.color,
                    '--animation-delay': `${index * 200}ms`
                  }}
                >
                  <div className="badge-icon">{badge.icon}</div>
                  <div className="badge-text">{badge.text}</div>
                  <div className="badge-glow"></div>
                </div>
              ))}
            </div>
            
            {/* Risk tolerance visualization */}
            <div className="risk-visualization">
              <h3>Your Risk Comfort Level</h3>
              <div className="risk-scale">
                <div className="risk-labels">
                  <span>Safe 🛡️</span>
                  <span>Balanced ⚖️</span>
                  <span>Adventurous 🚀</span>
                </div>
                <div className="risk-slider">
                  <div 
                    className="risk-indicator"
                    style={{ left: `${profile.riskTolerance?.score || 50}%` }}
                  >
                    <div className="indicator-dot"></div>
                    <div className="indicator-label">You</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="insights-slide">
            <div className="insights-header">
              <h2>What This Means For You</h2>
              <p>Here's how I'll personalize your investment journey:</p>
            </div>
            
            <div className="insights-list">
              {getPersonalizedInsights().map((insight, index) => (
                <div 
                  key={index} 
                  className="insight-card"
                  style={{ '--animation-delay': `${index * 300}ms` }}
                >
                  <div className="insight-icon">{insight.icon}</div>
                  <div className="insight-content">
                    <h4 className="insight-title">{insight.title}</h4>
                    <p className="insight-description">{insight.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="features-unlocked">
              <h3>🔓 Features Unlocked:</h3>
              <div className="feature-list">
                <div className="feature-item">🎯 Personalized stock recommendations</div>
                <div className="feature-item">📊 Custom learning pace</div>
                <div className="feature-item">💡 Smart investment tips</div>
                <div className="feature-item">🚀 Full trading access</div>
              </div>
            </div>
          </div>
        );

      case 'next_steps':
        return (
          <div className="next-steps-slide">
            <div className="next-steps-header">
              <div className="completion-avatar">🎯</div>
              <h2>You're All Set!</h2>
              <p>Ready to start your investment journey with personalized guidance?</p>
            </div>
            
            <div className="journey-preview">
              <h3>What's Next:</h3>
              <div className="journey-steps">
                <div className="journey-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Explore Your Dashboard</div>
                    <div className="step-description">See your personalized investment options</div>
                  </div>
                </div>
                <div className="journey-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Start Trading</div>
                    <div className="step-description">Make your first investment with my guidance</div>
                  </div>
                </div>
                <div className="journey-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Learn & Grow</div>
                    <div className="step-description">Ask me questions anytime you need help!</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="final-actions">
              <button 
                className="start-journey-btn"
                onClick={onContinue}
              >
                Start My Investment Journey! 🚀
              </button>
              
              <div className="gideon-farewell">
                <div className="farewell-bubble">
                  "I'm excited to help you become a successful investor! Let's do this! 🌟"
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="personality-insights-container">
      {/* Confetti animation */}
      {showConfetti && (
        <div className="confetti-container">
          {Array.from({ length: 50 }, (_, i) => (
            <div 
              key={i} 
              className="confetti-piece"
              style={{
                '--random-x': Math.random(),
                '--random-delay': Math.random() * 3000,
                '--random-duration': 3000 + Math.random() * 2000
              }}
            />
          ))}
        </div>
      )}
      
      {/* Main content */}
      <div className="insights-content">
        {renderSlide()}
      </div>
      
      {/* Slide indicators */}
      <div className="slide-indicators">
        {slides.map((_, index) => (
          <div 
            key={index}
            className={`slide-indicator ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
          />
        ))}
      </div>
      
      {/* Skip button for faster completion */}
      {currentSlide < slides.length - 1 && (
        <button 
          className="skip-button"
          onClick={() => setCurrentSlide(slides.length - 1)}
        >
          Skip to results ⏭️
        </button>
      )}
    </div>
  );
};

export default PersonalityInsights;